/**
 * StrugglingStudentsDrawer — slide-in drawer showing every user who rated
 * a given lesson in the current window. Lets admins one-click DM the user
 * with a pre-filled message so the heatmap doubles as an early-intervention
 * tool.
 *
 * Data source: GET /api/admin/confidence/heatmap/lesson/{lesson_id}?days=N
 * DM start:    POST /api/dm/conversations {peer_id, initial_message}
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../../components/ui/button';
import {
  X, MessageCircle, ExternalLink, HelpCircle, User as UserIcon, Loader2, Users, Send,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const rel = (iso) => {
  if (!iso) return '';
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return ''; }
};

// Face + tone matching the ConfidenceSurvey scale so admins recognise it.
const FACE = { 1: '😖', 2: '😕', 3: '🙂', 4: '😃', 5: '🤩' };
const TONE = {
  1: 'bg-rose-500/15 text-rose-700 border-rose-500/40',
  2: 'bg-rose-500/10 text-rose-700 border-rose-500/30',
  3: 'bg-amber-500/15 text-amber-700 border-amber-500/40',
  4: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/40',
  5: 'bg-emerald-500/20 text-emerald-800 border-emerald-500/50',
};

const DEFAULT_MESSAGE = (lessonTitle) =>
  `Hey — I noticed you flagged low confidence on "${lessonTitle}". `
  + `Happy to point you at a targeted resource or set up a 1:1 walkthrough. `
  + `What tripped you up the most?`;

const StrugglingStudentsDrawer = ({ open, onClose, lessonId, lessonTitle, days = 30 }) => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(null); // user_id currently being DM'd
  // Batch outreach state.
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState('struggling'); // 'struggling' | 'help' | 'all'
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeText, setComposeText] = useState('');
  const [broadcasting, setBroadcasting] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);
  const closeBtnRef = React.useRef(null);
  const prevFocusRef = React.useRef(null);

  useEffect(() => {
    if (!open || !lessonId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/admin/confidence/heatmap/lesson/${lessonId}?days=${days}`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) toast.error('Could not load student list'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, lessonId, days, API]);

  // Escape-to-close + focus management. Restore focus to the previously
  // focused element (typically the row/Students button) when we unmount.
  useEffect(() => {
    if (!open) return undefined;
    prevFocusRef.current = document.activeElement;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    document.addEventListener('keydown', onKey);
    // Focus close button on next tick so screen readers announce the dialog.
    setTimeout(() => { closeBtnRef.current && closeBtnRef.current.focus(); }, 20);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (prevFocusRef.current && typeof prevFocusRef.current.focus === 'function') {
        try { prevFocusRef.current.focus(); } catch (_) { /* noop */ }
      }
    };
  }, [open, onClose]);

  // Segment-filtered users so batch actions target only the intended cohort.
  const visibleUsers = useMemo(() => {
    const all = data?.users || [];
    if (filter === 'help') return all.filter((u) => (u.help_count || 0) > 0);
    if (filter === 'struggling') return all.filter((u) => u.struggling);
    return all;
  }, [data, filter]);

  const sendBroadcast = async () => {
    const peer_ids = Array.from(selected);
    if (peer_ids.length === 0 || !composeText.trim()) return;
    try {
      setBroadcasting(true);
      const { data: resp } = await axios.post(`${API}/dm/broadcast`, {
        peer_ids,
        message: composeText.trim(),
      });
      setBroadcastResult(resp);
      toast.success(`Sent to ${resp.sent_count} \u00b7 Skipped ${resp.skipped_count}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Broadcast failed';
      toast.error(msg);
    } finally {
      setBroadcasting(false);
    }
  };


  const startDm = async (u) => {
    try {
      setSending(u.user_id);
      const { data: resp } = await axios.post(`${API}/dm/conversations`, {
        peer_id: u.user_id,
        initial_message: DEFAULT_MESSAGE(lessonTitle || 'this lesson'),
      });
      const cid = resp?.conversation?.id;
      toast.success(`Message sent to ${u.user_name}`);
      if (cid) navigate(`/messages/${cid}`);
      else navigate('/messages');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not start conversation';
      toast.error(msg);
    } finally {
      setSending(null);
    }
  };

  if (!open) return null;

  return createPortal((
    <div
      className="fixed inset-0 z-[80] flex justify-end"
      role="dialog"
      aria-modal="true"
      data-testid="struggling-drawer"
    >
      <div
        className="absolute inset-0 bg-navy-950/70 backdrop-blur-sm"
        onClick={onClose}
        data-testid="struggling-drawer-backdrop"
      />
      <aside
        className="relative h-full w-full max-w-md bg-card border-l border-border shadow-2xl overflow-y-auto"
      >
        {/* Header */}
        <header className="sticky top-0 z-10 bg-card/95 backdrop-blur border-b border-border px-4 py-3 flex items-center gap-2">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">
              Struggling students
            </div>
            <div className="font-heading text-sm font-extrabold text-foreground truncate">
              {lessonTitle || 'Lesson detail'}
            </div>
            <div className="text-[10px] text-muted-foreground">
              Last {days} days · {data?.count ?? 0} rater{(data?.count ?? 0) === 1 ? '' : 's'}
            </div>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            className="ml-auto h-8 w-8 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/60"
            aria-label="Close"
            data-testid="struggling-drawer-close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-4">
          {loading && (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading students…
            </div>
          )}

          {!loading && data?.users?.length === 0 && (
            <div className="text-center py-10 text-sm text-muted-foreground border border-dashed border-border rounded-xl">
              <UserIcon className="w-6 h-6 mx-auto mb-2 opacity-50" />
              No ratings for this lesson in the last {days} days.
            </div>
          )}

          {!loading && (data?.users || []).length > 0 && (
            <>
              {/* Segment toolbar — filter + batch select + broadcast */}
              <div className="mb-3 rounded-xl border border-border bg-muted/30 p-2 flex items-center gap-2 flex-wrap" data-testid="segment-toolbar">
                <div className="inline-flex rounded-full border border-border overflow-hidden text-[10px] font-bold" role="tablist">
                  {['struggling', 'help', 'all'].map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => { setFilter(f); setSelected(new Set()); }}
                      data-testid={`segment-filter-${f}`}
                      className={`px-2.5 h-7 transition-colors uppercase tracking-widest ${
                        filter === f ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {f === 'struggling' ? 'Struggling' : f === 'help' ? 'Asked help' : 'All'}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (selected.size === visibleUsers.length) {
                      setSelected(new Set());
                    } else {
                      setSelected(new Set(visibleUsers.map((u) => u.user_id)));
                    }
                  }}
                  data-testid="segment-select-all"
                  className="text-[11px] font-bold text-primary hover:underline"
                >
                  {selected.size === visibleUsers.length && visibleUsers.length > 0 ? 'Clear' : 'Select all'} ({visibleUsers.length})
                </button>
                <Button
                  size="sm"
                  disabled={selected.size === 0}
                  onClick={() => {
                    setComposeText(`Hey — I noticed you flagged low confidence on "${lessonTitle || 'this lesson'}". `
                      + `Happy to point you at a targeted resource or set up a 1:1. What tripped you up the most?`);
                    setComposeOpen(true);
                  }}
                  data-testid="segment-compose-btn"
                  className="ml-auto rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  <Send className="w-3 h-3 mr-1" /> Message ({selected.size})
                </Button>
              </div>

              <ul className="space-y-2" data-testid="struggling-list">
                {visibleUsers.map((u) => (
                <li
                  key={u.user_id}
                  data-testid={`struggling-user-${u.user_id}`}
                  className={`rounded-xl border p-3 ${
                    u.struggling ? 'border-rose-300 bg-rose-50/50 dark:bg-rose-900/10' : 'border-border bg-muted/30'
                  } ${selected.has(u.user_id) ? 'ring-2 ring-primary/50' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Batch-select checkbox */}
                    <input
                      type="checkbox"
                      checked={selected.has(u.user_id)}
                      onChange={(e) => {
                        setSelected((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(u.user_id); else next.delete(u.user_id);
                          return next;
                        });
                      }}
                      disabled={u.user_status === 'banned' || u.user_status === 'suspended'}
                      data-testid={`segment-check-${u.user_id}`}
                      className="mt-1 h-4 w-4 accent-primary flex-shrink-0"
                      aria-label={`Select ${u.user_name}`}
                    />
                    {/* Avatar */}
                    <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {u.user_avatar
                        ? <img src={u.user_avatar} alt={u.user_name} className="h-full w-full object-cover" />
                        : <span className="font-bold text-sm">{(u.user_name || '?').slice(0, 2).toUpperCase()}</span>}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-foreground text-sm truncate">
                          {u.user_name || 'Unknown user'}
                        </div>
                        {u.user_status && u.user_status !== 'active' && (
                          <span className="text-[9px] uppercase tracking-widest font-bold bg-slate-500/15 text-slate-600 rounded px-1.5 py-0.5">
                            {u.user_status}
                          </span>
                        )}
                      </div>
                      {u.email && (
                        <div className="text-[11px] text-muted-foreground truncate">{u.email}</div>
                      )}

                      {/* Rating */}
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1 rounded-full border font-bold text-[11px] px-2 py-0.5 ${TONE[u.latest_confidence] || TONE[3]}`}>
                          <span aria-hidden>{FACE[u.latest_confidence] || '·'}</span>
                          Latest {u.latest_confidence ?? '?'}/5
                        </span>
                        <span className="text-[11px] font-bold text-muted-foreground">
                          {u.count} rating{u.count === 1 ? '' : 's'} · avg {u.avg_confidence}
                        </span>
                        {u.help_count > 0 && (
                          <span className="inline-flex items-center gap-1 rounded-full border font-bold text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary border-primary/30">
                            <HelpCircle className="w-2.5 h-2.5" /> asked for help
                          </span>
                        )}
                        <span className="text-[10px] text-muted-foreground ml-auto">
                          {rel(u.latest_at)}
                        </span>
                      </div>

                      {u.latest_notes && (
                        <blockquote className="mt-2 border-l-2 border-primary/40 pl-2 text-xs text-foreground/85 italic">
                          &ldquo;{u.latest_notes}&rdquo;
                        </blockquote>
                      )}

                      {/* Actions */}
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <Button
                          size="sm"
                          onClick={() => startDm(u)}
                          disabled={sending === u.user_id || u.user_status === 'banned' || u.user_status === 'suspended'}
                          data-testid={`struggling-dm-${u.user_id}`}
                          className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
                        >
                          {sending === u.user_id
                            ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Sending…</>
                            : <><MessageCircle className="w-3 h-3 mr-1" /> Send DM</>}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/u/${u.user_id}`)}
                          data-testid={`struggling-profile-${u.user_id}`}
                          className="rounded-full"
                        >
                          <ExternalLink className="w-3 h-3 mr-1" /> Profile
                        </Button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            </>
          )}
        </div>

        {/* Compose broadcast modal */}
        {composeOpen && (
          <div className="absolute inset-0 z-10 bg-navy-950/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="segment-compose-modal">
            <div className="w-full max-w-sm rounded-2xl bg-card border border-border shadow-2xl p-5">
              {!broadcastResult && (<>
                <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Batch message</div>
                <h3 className="font-heading text-base font-extrabold text-foreground mt-1">
                  Send to {selected.size} student{selected.size === 1 ? '' : 's'}
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  This will open (or reuse) a DM with each selected student and post the same message.
                </p>
                <textarea
                  value={composeText}
                  onChange={(e) => setComposeText(e.target.value)}
                  rows={5}
                  data-testid="segment-compose-textarea"
                  placeholder="Type your outreach message…"
                  className="mt-3 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary/60"
                />
                <div className="mt-4 flex items-center justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setComposeOpen(false); setComposeText(''); }}
                    disabled={broadcasting}
                    className="rounded-full font-bold"
                    data-testid="segment-compose-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={sendBroadcast}
                    disabled={broadcasting || !composeText.trim() || selected.size === 0}
                    data-testid="segment-compose-send"
                    className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    {broadcasting
                      ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Sending…</>
                      : <><Send className="w-3 h-3 mr-1" /> Send to {selected.size}</>}
                  </Button>
                </div>
              </>)}
              {broadcastResult && (<>
                <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-600">Broadcast complete</div>
                <h3 className="font-heading text-base font-extrabold text-foreground mt-1">
                  Sent to {broadcastResult.sent_count} · Skipped {broadcastResult.skipped_count}
                </h3>
                {broadcastResult.skipped_count > 0 && (
                  <ul className="mt-3 text-[11px] text-muted-foreground list-disc pl-4 max-h-24 overflow-y-auto">
                    {broadcastResult.skipped.map((s) => (
                      <li key={s.peer_id}><code>{s.peer_id.slice(0, 8)}</code> — {s.reason}</li>
                    ))}
                  </ul>
                )}
                <div className="mt-4 flex items-center justify-end">
                  <Button
                    size="sm"
                    onClick={() => { setBroadcastResult(null); setComposeOpen(false); setSelected(new Set()); }}
                    className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="segment-compose-done"
                  >
                    Done
                  </Button>
                </div>
              </>)}
            </div>
          </div>
        )}
      </aside>
    </div>
  ), document.body);
};

export default StrugglingStudentsDrawer;
